import React, { useEffect, useLayoutEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import Layout from '../components/layouts';

const LANG_STORAGE_KEY = 'futur-call-lang';

type Lang = 'en' | 'es';

type TermSection = {
  title: string;
  paragraphs?: string[];
  items?: string[];
  after?: string[];
};

const copy: Record<Lang, {
  pageTitle: string;
  meta: string;
  heading: string;
  kicker: string;
  intro: string;
  sections: TermSection[];
  backLabel: string;
  langLabel: string;
}> = {
  en: {
    pageTitle: 'Artist Terms & Conditions | TRASHed × FUTURFestival Mexico',
    meta: 'Artist terms and conditions for the TRASHed × FUTURFestival Mexico 2026 open call.',
    heading: 'Artist Terms & Conditions',
    kicker: 'TRASHed × FUTURFestival Mexico 2026',
    intro: 'Thanks for your interest in being part of TRASHed! Before submitting your artwork, please review a few important details about participating.',
    sections: [
      {
        title: '1. Who Can Participate',
        paragraphs: [
          'The open call is open to artists and collectives ages 18 and older.',
          'All submissions will be reviewed by the Global Inheritance and FUTURFestival Mexico teams based on creativity, originality, previous work, and how well the concept fits the festival and the TRASHed program.',
          'Submitting a concept does not guarantee selection.',
        ],
      },
      {
        title: '2. Artwork & Participation',
        paragraphs: ['Selected artists can participate in one of two ways:'],
        items: [
          'Pre-Painted Bins: Receive a blank recycling bin between October 30 and November 1, and return the finished artwork by November 10, 2026.',
          'Live Bin Battle: Paint a recycling bin live at FUTURFestival Mexico on November 14, 2026.',
        ],
        after: [
          'All artists will work with a 65-gallon recycling bin. Designs must cover the visible surface and use materials suitable for outdoor display.',
          'Since the finished bins will be donated to local schools, artwork must be appropriate for all ages and cannot include violent, sexual, discriminatory, or drug-related content.',
        ],
      },
      {
        title: '3. Artist Benefits',
        paragraphs: ['Selected participants will receive:'],
        items: [
          'Two VIP Premium festival passes, valid November 13–14, 2026.',
          'Four 50%-off discount codes for friends and family.',
          '$1,500 MXN toward art supplies.',
          'The opportunity to showcase their artwork at FUTURFestival Mexico and have it donated to a local school.',
        ],
        after: [
          'Live Bin Battle artists will also receive meals and refreshments during their painting session.',
          'The materials budget is intended for art supplies, not as payment for artistic services. Travel, transportation, and accommodations are not covered.',
        ],
      },
      {
        title: '4. Artwork Ownership & Usage',
        paragraphs: [
          'After the festival, completed recycling bins will become the property of the festival organizer and will be donated to elementary schools in Monterrey.',
          'Artists retain authorship of their original designs. By participating, artists give Global Inheritance and FUTURFestival Mexico permission to photograph, film, share, and promote their artwork through websites, social media, press, and other program-related communications without additional compensation.',
          'We love celebrating our artists and will make reasonable efforts to credit them whenever their work is featured.',
          'Artists confirm that their designs are original or that they have permission to use any third-party materials included.',
        ],
      },
      {
        title: '5. Artist Commitment',
        paragraphs: [
          'Selected artists agree to follow the program guidelines, meet production deadlines, and communicate with the organizing team.',
          'Global Inheritance and FUTURFestival Mexico reserve the right to request changes, reject artwork that doesn’t meet program guidelines, or remove participants who do not fulfill their commitments.',
        ],
      },
      {
        title: '6. Application & Agreement',
        paragraphs: [
          'Application deadline: October 27th, 2026.',
          'Submit your bin concept through the TRASHed Artist Form, and post your concept design on Instagram using #TRASHedxFutur and tagging @globalinheritance and @futurfestivalmexico.',
          'By submitting an application, you confirm that you meet the eligibility requirements and agree to these Terms & Conditions.',
          'Application information will be used to review submissions and coordinate the program in accordance with applicable privacy policies. Selected artists may be asked to sign additional participation agreements.',
        ],
      },
    ],
    backLabel: 'Back to the open call',
    langLabel: 'Language',
  },
  es: {
    pageTitle: 'Términos y condiciones para artistas | TRASHed × FUTURFestival Mexico',
    meta: 'Términos y condiciones para artistas de la convocatoria TRASHed × FUTURFestival Mexico 2026.',
    heading: 'Términos y condiciones para artistas',
    kicker: 'TRASHed × FUTURFestival Mexico 2026',
    intro: '¡Gracias por tu interés en formar parte de TRASHed! Antes de enviar tu obra, revisa estos detalles importantes sobre la participación.',
    sections: [
      {
        title: '1. Quién puede participar',
        paragraphs: [
          'La convocatoria está abierta a artistas y colectivos de 18 años o más.',
          'Todos los envíos serán revisados por los equipos de Global Inheritance y FUTURFestival Mexico según creatividad, originalidad, trabajo previo y qué tan bien encaja el concepto con el festival y el programa TRASHed.',
          'Enviar un concepto no garantiza la selección.',
        ],
      },
      {
        title: '2. Obra y participación',
        paragraphs: ['Las y los artistas seleccionados pueden participar de una de estas dos formas:'],
        items: [
          'Botes pre-pintados: reciben un bote de reciclaje en blanco entre el 30 de octubre y el 1 de noviembre, y entregan la obra terminada el 10 de noviembre de 2026.',
          'Bin Battle en vivo: pintan un bote de reciclaje en vivo en FUTURFestival Mexico el 14 de noviembre de 2026.',
        ],
        after: [
          'Todas y todos los artistas trabajarán con un bote de reciclaje de 65 galones. Los diseños deben cubrir la superficie visible y usar materiales aptos para exhibición al aire libre.',
          'Como los botes terminados se donarán a escuelas locales, la obra debe ser apropiada para todas las edades y no puede incluir contenido violento, sexual, discriminatorio o relacionado con drogas.',
        ],
      },
      {
        title: '3. Beneficios para artistas',
        paragraphs: ['Las y los participantes seleccionados recibirán:'],
        items: [
          'Dos pases VIP Premium del festival, válidos el 13 y 14 de noviembre de 2026.',
          'Cuatro códigos de descuento del 50% para amistades y familiares.',
          '$1,500 MXN para materiales de arte.',
          'La oportunidad de exhibir su obra en FUTURFestival Mexico y donarla a una escuela local.',
        ],
        after: [
          'Las y los artistas del Bin Battle en vivo también recibirán alimentos e hidratación durante su sesión de pintura.',
          'El presupuesto de materiales es para insumos de arte, no un pago por servicios artísticos. No se cubren viaje, transporte ni hospedaje.',
        ],
      },
      {
        title: '4. Titularidad y uso de la obra',
        paragraphs: [
          'Después del festival, los botes de reciclaje terminados pasarán a ser propiedad del organizador del festival y se donarán a escuelas primarias de Monterrey.',
          'Las y los artistas conservan la autoría de sus diseños originales. Al participar, otorgan a Global Inheritance y a FUTURFestival Mexico permiso para fotografiar, filmar, compartir y promover su obra en sitios web, redes sociales, prensa y otras comunicaciones del programa, sin compensación adicional.',
          'Nos encanta celebrar a nuestras y nuestros artistas y haremos esfuerzos razonables por dar crédito cuando se presente su trabajo.',
          'Las y los artistas confirman que sus diseños son originales o que tienen permiso para usar cualquier material de terceros incluido.',
        ],
      },
      {
        title: '5. Compromiso de la o el artista',
        paragraphs: [
          'Las y los artistas seleccionados aceptan seguir los lineamientos del programa, cumplir las fechas de producción y comunicarse con el equipo organizador.',
          'Global Inheritance y FUTURFestival Mexico se reservan el derecho de solicitar cambios, rechazar obra que no cumpla los lineamientos del programa o dar de baja a participantes que no cumplan sus compromisos.',
        ],
      },
      {
        title: '6. Solicitud y acuerdo',
        paragraphs: [
          'Fecha límite de solicitud: 27 de octubre de 2026.',
          'Envía el concepto de tu bote a través del formulario de artista TRASHed y publica tu diseño en Instagram con #TRASHedxFutur, etiquetando a @globalinheritance y @futurfestivalmexico.',
          'Al enviar una solicitud, confirmas que cumples los requisitos de elegibilidad y aceptas estos Términos y condiciones.',
          'La información de la solicitud se usará para revisar envíos y coordinar el programa de acuerdo con las políticas de privacidad aplicables. Es posible que se pida a las y los artistas seleccionados firmar acuerdos de participación adicionales.',
        ],
      },
    ],
    backLabel: 'Volver a la convocatoria',
    langLabel: 'Idioma',
  },
};

const useIsoLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

function preferredLang(): Lang {
  if (typeof window === 'undefined') return 'en';
  try {
    const saved = window.localStorage.getItem(LANG_STORAGE_KEY);
    if (saved === 'en' || saved === 'es') return saved;
  } catch (err) {
    // Private browsing can block storage; fall through to the browser language.
  }
  const list = (navigator.languages && navigator.languages.length
    ? navigator.languages
    : [navigator.language]
  ).filter(Boolean);
  return list.some((code) => code.toLowerCase().startsWith('es')) ? 'es' : 'en';
}

const FuturCallTerms = () => {
  const [lang, setLang] = useState<Lang | null>(null);
  const activeLang: Lang = lang || 'en';
  const text = copy[activeLang];

  useIsoLayoutEffect(() => {
    setLang(preferredLang());
  }, []);

  const chooseLang = (next: Lang) => {
    setLang(next);
    try {
      window.localStorage.setItem(LANG_STORAGE_KEY, next);
    } catch (err) {
      // Ignore storage failures; the in-page choice still applies.
    }
  };

  return (
    <Layout navBg>
      <Helmet htmlAttributes={{ lang: activeLang }}>
        <title>{text.pageTitle}</title>
        <meta name="description" content={text.meta} />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=Funnel+Sans:wght@300;400;500;600;700;800&display=swap"
          rel="stylesheet"
        />
        <link href="https://fonts.cdnfonts.com/css/sweet-sans-pro" rel="stylesheet" />
      </Helmet>

      <div className="futur-call-page futur-call-terms-page">
        <div className="futur-call-lang-bar">
          <div className="futur-call-lang" role="group" aria-label={text.langLabel}>
            <button type="button" aria-pressed={lang === 'en'} onClick={() => chooseLang('en')}>
              EN
            </button>
            <button type="button" aria-pressed={lang === 'es'} onClick={() => chooseLang('es')}>
              ES
            </button>
          </div>
        </div>

        <section className="futur-call-body">
          <div className="futur-call-terms">
            <p className="futur-call-apply-label">{text.kicker}</p>
            <h1>{text.heading}</h1>
            <p>{text.intro}</p>

            {text.sections.map((section) => (
              <div key={section.title} className="futur-call-terms-section">
                <h2>{section.title}</h2>
                {section.paragraphs && section.paragraphs.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
                {section.items && (
                  <ul>
                    {section.items.map((entry) => (
                      <li key={entry}>{entry}</li>
                    ))}
                  </ul>
                )}
                {section.after && section.after.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </div>
            ))}

            <p className="futur-call-terms-back">
              <a href="/futur-call-to-artists">{text.backLabel}</a>
            </p>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default FuturCallTerms;
